import { PerspectiveCamera, Vector3 } from 'three';

let camera = null;
let shakeAmount = 0;
let cameraZoom = 0;
let baseFov = 62;

const scratchBase = new Vector3();
const scratchTargetCamera = new Vector3();
const scratchTargetLook = new Vector3();
const scratchTopCamera = new Vector3();
const scratchThirdCamera = new Vector3();
const scratchTopLook = new Vector3();
const scratchThirdLook = new Vector3();

export function initCamera(options = {}) {
  const {
    fov = 45,
    aspect = (window.innerWidth || 1) / (window.innerHeight || 1),
    near = 0.1,
    far = 220,
    position = [0, 40, 42],
    lookAt = [0, 0, 0],
    listenForResize = true,
  } = options;

  camera = new PerspectiveCamera(fov, aspect, near, far);
  baseFov = fov;
  camera.position.set(position[0], position[1], position[2]);
  camera.lookAt(lookAt[0], lookAt[1], lookAt[2]);

  if (listenForResize) {
    window.addEventListener('resize', resizeCamera);
  }

  return camera;
}

export function getCamera() {
  if (!camera) {
    throw new Error('[camera] initCamera() must run before getCamera().');
  }
  return camera;
}

export function resizeCamera(width = window.innerWidth || 1, height = window.innerHeight || 1) {
  if (!camera) return;

  camera.aspect = width / Math.max(1, height);
  camera.updateProjectionMatrix();
}

export function setCameraFov(fov) {
  const activeCamera = getCamera();
  baseFov = fov;
  activeCamera.fov = fov - cameraZoom * 14;
  activeCamera.updateProjectionMatrix();
}

export function addCameraShake(amount, enabled = true) {
  if (!enabled) return shakeAmount;
  shakeAmount = Math.min(1.35, Math.max(shakeAmount, amount));
  return shakeAmount;
}

export function adjustCameraZoom(direction = 0) {
  cameraZoom = Math.min(1, Math.max(0, cameraZoom + Math.sign(direction) * 0.25));
  return cameraZoom;
}

export function setCameraZoom(value = 0) {
  cameraZoom = Math.min(1, Math.max(0, value));
  return cameraZoom;
}

export function updateCameraFollow(dt, player, options = {}) {
  const activeCamera = getCamera();
  const {
    shakeEnabled = true,
    followSpeed = 4.8,
    cameraHeight = 39,
    cameraDistance = 39,
    lookHeight = 0.6,
    lookAhead = 2.6,
    shakeScale = 1,
  } = options;

  scratchBase.set(player.pos.x, 0, player.pos.z);
  const aimX = Math.abs(player.aim.x) + Math.abs(player.aim.z) > 0.001 ? player.aim.x : 0;
  const aimZ = Math.abs(player.aim.x) + Math.abs(player.aim.z) > 0.001 ? player.aim.z : 1;
  scratchTopCamera.set(scratchBase.x, cameraHeight, scratchBase.z + cameraDistance);
  const shoulderX = -aimZ * 1.65;
  const shoulderZ = aimX * 1.65;
  scratchThirdCamera.set(
    scratchBase.x - aimX * 11.5 + shoulderX,
    6.8,
    scratchBase.z - aimZ * 11.5 + shoulderZ,
  );
  scratchTopLook.set(
    scratchBase.x + player.aim.x * lookAhead,
    lookHeight,
    scratchBase.z + player.aim.z * lookAhead,
  );
  scratchThirdLook.set(
    scratchBase.x + aimX * 12.5,
    1.55,
    scratchBase.z + aimZ * 12.5,
  );
  const t = cameraZoom * cameraZoom * (3 - 2 * cameraZoom);
  activeCamera.fov += ((baseFov - t * 14) - activeCamera.fov) * (1 - Math.exp(-dt * 5.2));
  activeCamera.updateProjectionMatrix();
  scratchTargetCamera.lerpVectors(scratchTopCamera, scratchThirdCamera, t);
  scratchTargetLook.lerpVectors(scratchTopLook, scratchThirdLook, t);

  const follow = 1 - Math.exp(-dt * followSpeed);
  activeCamera.position.lerp(scratchTargetCamera, follow);

  shakeAmount = Math.max(0, shakeAmount - dt * (1.2 + shakeAmount * 2.8));
  const jitter = shakeEnabled && shakeAmount > 0.001 ? shakeAmount * shakeScale * 2.35 : 0;
  activeCamera.position.x += (Math.random() - 0.5) * jitter;
  activeCamera.position.y += (Math.random() - 0.5) * jitter * 0.45;
  activeCamera.position.z += (Math.random() - 0.5) * jitter;
  activeCamera.lookAt(scratchTargetLook);
  if (jitter > 0) activeCamera.rotation.z += (Math.random() - 0.5) * jitter * 0.032;

  return shakeAmount;
}

export function disposeCamera() {
  window.removeEventListener('resize', resizeCamera);
  camera = null;
  shakeAmount = 0;
  cameraZoom = 0;
}
